import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { MultiSelect, type LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useMemo } from 'react';
import { useBankAccountsFilters } from '../../hooks/useBankAccountsFilters';

interface BankAccountsFiltersPanelProps {
  initialConditions: FilterConditionOutput[];
  onChange: (conditions: FilterConditionOutput[]) => void;
  teamIds?: string[];
  onTeamIdsChange?: (teamIds: string[]) => void;
}

export const BankAccountsFiltersPanel = ({
  initialConditions,
  onChange,
  teamIds = [],
  onTeamIdsChange,
}: BankAccountsFiltersPanelProps) => {
  const { t } = useTranslation('bankAccountsModule');
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);

  const loadTeamsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      const queryParams: Record<string, number | string> = {
        page: params.page,
        per_page: params.limit,
      };
      if (params.search) queryParams.name = params.search;
      const response = await httpClient.get<{
        data?: Record<string, unknown>[];
        meta?: Record<string, unknown>;
      }>('/teams-child', queryParams);
      const list = Array.isArray(response) ? (response as unknown[]) : (response.data ?? []);
      const meta = !Array.isArray(response) ? (response.meta ?? {}) : {};
      const currentPage = Number(meta.current_page ?? meta.currentPage ?? 1);
      const lastPage = Number(meta.last_page ?? meta.lastPage ?? 1);
      return {
        options: list.map((row: Record<string, unknown>) => ({
          value: String(row.id),
          label: String(row.name ?? row.id),
        })),
        hasMore: meta ? currentPage < lastPage : false,
      };
    },
    [httpClient],
  );

  const { filters, handleFilterChange, clearFilters } = useBankAccountsFilters({
    initialConditions,
    onChange,
  });

  const handleClearAll = () => {
    clearFilters();
    onTeamIdsChange?.([]);
  };

  const hasTeamSelection = teamIds.length > 0;

  const statusOptions = [
    { label: t('status.pending', 'Pendiente'), value: 'PENDING' },
    { label: t('status.verified', 'Verificado'), value: 'VERIFIED' },
    { label: t('status.rejected', 'Rechazado'), value: 'REJECTED' },
    { label: t('status.disabled', 'Deshabilitado'), value: 'DISABLED' },
  ];

  return (
    <div className="space-y-4 min-w-[300px]">
      {/* Cliente / equipo — mismo patrón que liquidaciones (MultiSelect + /teams-child) */}
      <div className="space-y-2">
        <Label>{t('filters.team.label', 'Cliente')}</Label>
        <MultiSelect
          loadOptions={loadTeamsForMultiSelect}
          value={teamIds}
          onValueChange={(vals) => onTeamIdsChange?.(vals)}
          placeholder={t('filters.team.placeholder', 'Todos los clientes')}
          emptyText={t('filters.team.empty', 'Sin resultados')}
          noResultsText={t('filters.team.noResults', 'Sin resultados')}
          searchPlaceholder={t('filters.team.searchPlaceholder', 'Buscar clientes...')}
          dataTestId="bank-accounts-filter-team"
          triggerClassName="h-[38px]"
          pageSize={20}
        />
      </div>

      {/* Status */}
      <div className="space-y-2">
        <Label>{t('filters.status.label', 'Estado')}</Label>
        <MultiSelect
          options={statusOptions}
          value={filters.status}
          onValueChange={(val) => handleFilterChange('status', val)}
          placeholder={t('filters.status.placeholder', 'Selecciona estados')}
          variant="inverted"
        />
      </div>

      {/* Country */}
      <div className="space-y-2">
        <Label>{t('filters.country.label', 'País')}</Label>
        <Input
          placeholder={t('filters.country.placeholder', 'Código de país')}
          value={filters.country}
          onChange={(e) => handleFilterChange('country', e.target.value)}
        />
      </div>

      {/* Clear Filters Button */}
      <div className="pt-2">
        <Button
          variant="outline"
          className="w-full"
          onClick={handleClearAll}
          disabled={!filters.status.length && !filters.country && !hasTeamSelection}
        >
          {t('filters.clear', 'Limpiar filtros')}
        </Button>
      </div>
    </div>
  );
};
